import { useEffect, useCallback, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, ZoomIn, ZoomOut, FileDown } from "lucide-react";
import { imageUrl, thumbUrl, productPdfUrl } from "../config";
import { QuoteModal } from "./QuoteModal";
import { MobileProductFeed } from "./MobileProductFeed";
import { useLang, productGroupLabel, productDesc } from "../i18n";

export const ProductLightbox = ({ products, index, onClose, onNavigate }) => {
  const open = index !== null && index >= 0;
  const product = open ? products[index] : null;
  const { lang, t, dn } = useLang();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  const [scale, setScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [smooth, setSmooth] = useState(true);
  const [hiResLoaded, setHiResLoaded] = useState(false);
  const zoomed = scale > 1.02;
  const ZOOM = 2.6;

  const pinchRef = useRef(null);
  const lastTapRef = useRef(0);
  const tapPosRef = useRef({ x: 50, y: 50 });
  const movedRef = useRef(false);
  const wasTouchRef = useRef(false);

  const clampPct = (v) => Math.max(0, Math.min(100, v));

  const pointFromEvent = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const p = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: clampPct(((p.clientX - rect.left) / rect.width) * 100), y: clampPct(((p.clientY - rect.top) / rect.height) * 100) };
  };

  const midFromTouches = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const [a, b] = [e.touches[0], e.touches[1]];
    return { x: clampPct((((a.clientX + b.clientX) / 2 - rect.left) / rect.width) * 100), y: clampPct((((a.clientY + b.clientY) / 2 - rect.top) / rect.height) * 100) };
  };

  const touchDist = (e) => Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);

  // Masaüstü: tek tıkla yakınlaştır/uzaklaştır
  const handleClick = (e) => {
    e.stopPropagation();
    if (wasTouchRef.current) { wasTouchRef.current = false; return; }
    setSmooth(true);
    if (zoomed) { setScale(1); }
    else { setOrigin(pointFromEvent(e)); setScale(ZOOM); }
  };

  // Masaüstü: yakınken imleçle gezin
  const handleMouseMove = (e) => {
    if (!zoomed || e.pointerType === "touch") return;
    setOrigin(pointFromEvent(e));
  };

  const handleTouchStart = (e) => {
    wasTouchRef.current = true;
    if (e.touches.length === 2) {
      pinchRef.current = { startDist: touchDist(e), startScale: scale };
      setSmooth(false);
      setOrigin(midFromTouches(e));
      movedRef.current = true;
    } else if (e.touches.length === 1) {
      movedRef.current = false;
      tapPosRef.current = pointFromEvent(e);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const ratio = touchDist(e) / (pinchRef.current.startDist || 1);
      setScale(Math.max(1, Math.min(4, pinchRef.current.startScale * ratio)));
      setOrigin(midFromTouches(e));
      movedRef.current = true;
    } else if (e.touches.length === 1 && zoomed) {
      setOrigin(pointFromEvent(e));
      movedRef.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (pinchRef.current && e.touches.length < 2) {
      pinchRef.current = null;
      setSmooth(true);
      if (scale < 1.1) setScale(1);
      return;
    }
    if (movedRef.current) { movedRef.current = false; return; }
    // Çift dokunuşla yakınlaştır/uzaklaştır
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      setSmooth(true);
      if (zoomed) setScale(1);
      else { setOrigin(tapPosRef.current); setScale(ZOOM); }
    } else {
      lastTapRef.current = now;
    }
  };

  const goContact = () => setQuoteOpen(true);

  const goPrev = useCallback(() => {
    if (!open) return;
    onNavigate((index - 1 + products.length) % products.length);
  }, [open, index, products.length, onNavigate]);

  const goNext = useCallback(() => {
    if (!open) return;
    onNavigate((index + 1) % products.length);
  }, [open, index, products.length, onNavigate]);

  useEffect(() => {
    if (!open) setQuoteOpen(false);
  }, [open]);

  // Ürün değişince veya lightbox kapanınca yakınlaştırmayı sıfırla
  useEffect(() => {
    setScale(1);
    setSmooth(true);
    setOrigin({ x: 50, y: 50 });
    setHiResLoaded(false);
    pinchRef.current = null;
    lastTapRef.current = 0;
  }, [index, open]);

  // Komşu (önceki/sonraki) görselleri önden yükle → ok navigasyonu anında
  useEffect(() => {
    if (!open || products.length < 2) return;
    const preload = (i) => {
      const p = products[i];
      if (p && p.image) { const im = new Image(); im.src = imageUrl(p.image); }
    };
    preload((index + 1) % products.length);
    preload((index - 1 + products.length) % products.length);
  }, [open, index, products]);

  useEffect(() => {
    if (!open || isMobile) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (quoteOpen) setQuoteOpen(false);
        else onClose();
      } else if (!quoteOpen && e.key === "ArrowLeft") goPrev();
      else if (!quoteOpen && e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, isMobile, onClose, goPrev, goNext, quoteOpen]);

  // Mobil: dikey akış — kaydırdıkça diğer ürünler devam eder
  if (isMobile) {
    return <MobileProductFeed products={products} index={index} onClose={onClose} />;
  }

  return createPortal(
    <AnimatePresence>
      {open && product && (
        <motion.div
          data-testid="product-lightbox"
          className="fixed inset-0 z-[130] flex items-center justify-center p-2 md:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#0D0C0B]/90 backdrop-blur-xl" />

          {/* Close */}
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Kapat"
            className="absolute top-4 right-4 md:top-8 md:right-8 z-30 w-14 h-14 md:w-11 md:h-11 rounded-full bg-[#0D0C0B]/70 backdrop-blur-sm border border-white/30 flex items-center justify-center text-[#F2EFEB] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300"
          >
            <X strokeWidth={1.75} className="w-7 h-7 md:w-[18px] md:h-[18px]" />
          </button>

          {/* Prev / Next */}
          {products.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                aria-label="Önceki"
                className="absolute left-3 md:left-8 top-[22vh] md:top-1/2 -translate-y-1/2 z-20 w-14 h-14 md:w-11 md:h-11 rounded-full bg-[#0D0C0B]/70 backdrop-blur-sm border border-white/30 flex items-center justify-center text-[#F2EFEB] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300"
              >
                <ArrowLeft strokeWidth={1.75} className="w-7 h-7 md:w-[18px] md:h-[18px]" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                aria-label="Sonraki"
                className="absolute right-3 md:right-8 top-[22vh] md:top-1/2 -translate-y-1/2 z-20 w-14 h-14 md:w-11 md:h-11 rounded-full bg-[#0D0C0B]/70 backdrop-blur-sm border border-white/30 flex items-center justify-center text-[#F2EFEB] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300"
              >
                <ArrowRight strokeWidth={1.75} className="w-7 h-7 md:w-[18px] md:h-[18px]" />
              </button>
            </>
          )}

          {/* Content */}
          <motion.div
            key={product.id}
            className="relative z-10 w-full max-w-6xl flex flex-col md:grid md:grid-cols-[1.4fr_1fr] gap-0 bg-white ring-1 ring-black/10 rounded-lg overflow-hidden max-h-[96vh] md:max-h-[88vh]"
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div
              data-testid="lightbox-image-zoom"
              onClick={handleClick}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ touchAction: "none" }}
              className={`relative bg-[#F0EDE8] h-[44vh] shrink-0 md:h-auto md:min-h-[60vh] overflow-hidden select-none ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
            >
              {product.image ? (
                <div
                  className="absolute inset-0 will-change-transform"
                  style={{
                    transformOrigin: `${origin.x}% ${origin.y}%`,
                    transform: `scale(${scale})`,
                    transition: smooth ? "transform 0.45s cubic-bezier(0.22,1,0.36,1)" : "none",
                  }}
                >
                  {/* Hızlı önizleme (grid'den önbellekli thumbnail) — anında görünür */}
                  <img
                    src={thumbUrl(product.image)}
                    alt={product.name}
                    draggable={false}
                    aria-hidden="true"
                    className="w-full h-full object-contain absolute inset-0"
                  />
                  {/* Tam çözünürlük — yüklenince yumuşakça belirir */}
                  <img
                    key={product.image}
                    src={imageUrl(product.image)}
                    alt={product.name}
                    draggable={false}
                    onLoad={() => setHiResLoaded(true)}
                    style={{ opacity: hiResLoaded ? 1 : 0, transition: "opacity 0.35s ease-out" }}
                    className="w-full h-full object-contain absolute inset-0"
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#8C8681] text-xs absolute inset-0">{t("common.noImage")}</div>
              )}

              {/* Yakınlaştırma ipucu / durum rozeti */}
              {product.image && (
                <div
                  data-testid="lightbox-zoom-hint"
                  className="absolute bottom-4 left-4 z-10 flex items-center gap-2 rounded-full bg-[#0D0C0B]/70 backdrop-blur-sm border border-white/20 px-3.5 py-2 text-[#F2EFEB] pointer-events-none"
                >
                  {zoomed ? <ZoomOut size={14} strokeWidth={1.75} /> : <ZoomIn size={14} strokeWidth={1.75} />}
                  <span className="font-sans uppercase tracking-[0.18em] text-[9px]">
                    {zoomed ? t("lightbox.zoomOut") : t("lightbox.zoomIn")}
                  </span>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="relative p-6 md:p-10 flex flex-col overflow-hidden flex-1 min-h-0">
              <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  <span className="h-px w-8 bg-[#7BA05B]" />
                  <span className="font-sans uppercase tracking-[0.25em] text-[10px] text-[#2F4A26]">
                    {productGroupLabel(product, dn)}
                  </span>
                </div>
                <h3 data-testid="lightbox-title" translate="no" className="notranslate font-serif text-2xl md:text-4xl text-[#1A1715] tracking-tight leading-tight">
                  {product.name}
                </h3>
                {product.category !== "Yapay Kaplama" && (
                  productDesc(product, lang) ? (
                    <p data-testid="lightbox-description" className="text-[#6B6560] leading-relaxed mt-4 md:mt-5 text-sm md:text-base whitespace-pre-line">
                      {productDesc(product, lang)}
                    </p>
                  ) : (
                    <p className="text-[#6B6560]/70 leading-relaxed mt-4 md:mt-5 text-sm italic">
                      {t("lightbox.noDesc")}
                    </p>
                  )
                )}
              </div>

              <div className="shrink-0 mt-4 md:mt-6 pt-4 md:pt-5 border-t border-black/10 flex items-center justify-between">
                <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#6B6560]">
                  {String(index + 1).padStart(2, "0")} / {String(products.length).padStart(2, "0")}
                </span>
                <div className="flex items-center gap-5">
                  {product.image && product.image.startsWith("/api/files/") && (
                    <a
                      href={productPdfUrl(product, lang)}
                      download
                      onClick={(e) => e.stopPropagation()}
                      data-testid="lightbox-pdf"
                      className="group inline-flex items-center gap-2 font-sans uppercase tracking-[0.15em] text-[11px] text-[#6B6560] hover:text-[#2F4A26] transition-colors"
                    >
                      <FileDown size={14} strokeWidth={1.5} className="group-hover:-translate-y-0.5 transition-transform" />
                      {t("lightbox.pdf")}
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={goContact}
                    data-testid="lightbox-contact"
                    className="group inline-flex items-center gap-2 font-sans uppercase tracking-[0.15em] text-[11px] text-[#1A1715] hover:text-[#2F4A26] transition-colors"
                  >
                    {t("lightbox.quote")}
                    <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Teklif Al — ortak iletişim formu */}
          <QuoteModal product={product} open={quoteOpen} onClose={() => setQuoteOpen(false)} />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
